// The first visit's Highlights strip (ba-web; the owner's ask of 2026-10-02, the lead's go of 10-06): over the root page, one
// card per scene from the Gallery's own cards (data/highlights.ts), each opening its exact state the way the Gallery does. It
// is up on the bare root address or with `highlights=1` (main.tsx decides at boot), once per session (sessionStorage, as the
// 2D notice's), and never over a deep link's state, in presentation, a tour or an open sheet. A card, "All in the Gallery",
// the × or Escape (app/shortcuts.ts) closes it for the session. It sits as a tray above the bottom row, clear of the scene's
// panel, and scrolls sideways when the cards don't fit; it adds nothing to the bar (design spec §3).
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useAtlas } from '../app/store.ts';
import { loadGallery, type Gallery as GalleryData, type GalleryItem } from '../data/gallery.ts';
import { HIGHLIGHTS, HIGHLIGHTS_SEEN, sceneOfLink } from '../data/highlights.ts';
import { SCENARIOS } from '../scenarios/index.ts';
import { Button, IconButton } from '../ui/primitives.tsx';
import { openLink, Poster } from './Gallery.tsx';
import './web.css';

/** Whether this session has dismissed or used the strip. */
export function highlightsSeen(): boolean {
  try {
    return sessionStorage.getItem(HIGHLIGHTS_SEEN) === '1';
  } catch {
    return false;
  }
}

/** Close the strip for the session (storage refused, in a private window say: it comes back on a reload). */
export function dismissHighlights(): void {
  try {
    sessionStorage.setItem(HIGHLIGHTS_SEEN, '1');
  } catch {
    // The flag is a convenience; the strip is closed either way.
  }
  useAtlas.getState().setHighlights(false);
}

export function Highlights() {
  const open = useAtlas((s) => s.highlightsOpen);
  const covered = useAtlas((s) => s.galleryOpen || s.paletteOpen || s.helpOpen || s.tour !== null || s.presenting);
  const [data, setData] = useState<GalleryData | null>(null);
  useEffect(() => {
    if (open) void loadGallery().then(setData);
  }, [open]);
  if (!open || covered) return null;
  const cards = HIGHLIGHTS.map((id) => data?.items.find((it) => it.id === id) ?? null).filter((it): it is GalleryItem => it !== null);
  const openCard = (it: GalleryItem) => {
    const link = it.links[0];
    if (!link) return;
    dismissHighlights();
    openLink(link.q);
  };
  const toGallery = () => {
    dismissHighlights();
    useAtlas.getState().setGallery(true);
  };
  return (
    <section className="highlights" role="region" aria-label="Highlights" data-label-exclude="">
      <header className="highlights-head">
        <span className="highlights-title ba-type-body-strong">Highlights</span>
        <span className="highlights-sub ba-type-small muted">Nine scenes, one state each, live in the app</span>
        <Button variant="quiet" onClick={toGallery}>
          All in the Gallery
        </Button>
        <IconButton size="sm" icon={X} label="Close the highlights" onClick={dismissHighlights} />
      </header>
      {data ? (
        <ul className="highlights-list" aria-label="One state per scene">
          {cards.map((it) => {
            const scene = SCENARIOS.find((sc) => sc.id === sceneOfLink(it.links[0]?.q ?? ''));
            return (
              <li key={it.id}>
                <button type="button" className="hcard" data-scene={scene?.id ?? ''} onClick={() => openCard(it)} aria-label={`${it.title}: open in ${scene?.name ?? 'the app'}`}>
                  <Poster g={data} it={it} />
                  <span className="hcard-text">
                    {scene ? (
                      <span className="hcard-scene ba-type-caption" style={{ ['--sc' as string]: `var(--sc-${scene.id})` }}>
                        <span className="dot" aria-hidden="true" />
                        {scene.name}
                      </span>
                    ) : null}
                    <span className="hcard-title ba-type-body-strong">{it.title}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="highlights-empty ba-type-small muted">Loading the gallery</p>
      )}
    </section>
  );
}
